import LayoutContainer from 'components/Common/LayoutContainer'
import { OfferListEvents, OfferListEventsProvider, type OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { rem } from 'polished'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { useCallback, useEffect, useRef, type MouseEvent } from 'react'
import { useInView } from 'react-intersection-observer'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { trackClientEvent } from 'analytics/analytics'
import type { ArrowDirection } from 'LuxKit/Carousel/CardCarousel'
import ExperienceListTileCarousel from '../ExperienceLists/ExperienceListTileCarousel'
import useExperienceOfferPageRecommendations, { type RecommendationId, type RecommendationSection } from 'hooks/Experiences/useExperienceOfferPageRecommendations'

const RecommendationsContainer = styled.div`
  padding-block: ${rem(40)};

  > * + * {
    margin-block-start: ${rem(32)};
  }
`

const Section = styled(LayoutContainer)`
  > * + * {
    margin-block-start: ${rem(32)};
  }
`

const titles = defineMessages({
  yaml: {
    id: '6LtmjrSHCkk4gMps69nw',
    defaultMessage: 'You may also like',
    description: 'Section heading for recommended similar experiences on PDP',
  },
  similar: {
    id: 'EB13LAC940JtBHgosvho',
    defaultMessage: 'Explore similar offers',
    description: 'Heading for experience recommendations in the same parent category on the offer page',
  },
  new: {
    id: '7tj-a860yyg39Yfa2sQn',
    defaultMessage: 'Try something new',
    description: 'Heading for experience recommendations excluding travel essentials on the offer page',
  },
  dining: {
    id: 'Pd11_aBX7RXHxIz_IWnd',
    defaultMessage: 'Feeling fancy?',
    description: 'Heading for wine and dining experience recommendations on the experience offer page',
  },
  wellness: {
    id: 'J-eivNSuhxD9amW_4aLl',
    defaultMessage: 'Treat yourself',
    description: 'Heading for spa and wellness experience recommendations on the experience offer page',
  },
  activities: {
    id: 'a7OWRVLSHPg08whWVqr8',
    defaultMessage: 'Feeling adventurous?',
    description: 'Heading for top activities recommendations below reviews on the experience offer page',
  },
})

const trackingNames: Record<RecommendationId, string> = {
  yaml: 'YAML Carousel',
  similar: 'PDP Explore Similar Offers Carousel',
  new: 'PDP Try Something New Carousel',
  dining: 'PDP Feeling Fancy Carousel',
  wellness: 'PDP Treat Yourself Carousel',
  activities: 'PDP Feeling Adventurous Carousel',
}

const trackingSubjects: Record<RecommendationId, string> = {
  yaml: 'you_may_also_like',
  similar: 'explore_similar_offers',
  new: 'try_something_new',
  dining: 'feeling_fancy',
  wellness: 'treat_yourself',
  activities: 'feeling_adventurous',
}

type RecommendationTrackingAction = 'impression' | 'tile_clicked' | 'view_all_clicked' | 'carousel_next_clicked' | 'carousel_prev_clicked'

interface SectionProps {
  section: RecommendationSection
  place: App.Place
  experienceId: string
}

function RecommendationCarousel({ section, place, experienceId }: SectionProps) {
  const intl = useIntl()
  const [searchUrl] = useExperienceSearchUrl({ place, categoryIds: section.categoryIds })
  const tracking = useOfferListTracking(getListName('OfferList', 'EXPERIENCES', trackingNames[section.id]), { key: experienceId })
  const [trackingRef, inView] = useInView({ threshold: 0.5 })
  const impressedList = useRef<string | undefined>(undefined)
  const trackInteraction = useCallback((action: RecommendationTrackingAction, itemPosition?: number) => {
    const listItem = itemPosition === undefined ? undefined : comluxgroup.createListItem_1_0_0({
      listName: tracking.listName,
      listInstance: tracking.listId,
      itemPosition,
    })
    trackClientEvent({
      category: 'experiences_offer_page_recommendations',
      type: 'interaction',
      subject: trackingSubjects[section.id],
      action,
      ...(listItem && { context: [listItem] }),
    })
  }, [section.id, tracking.listId, tracking.listName])

  useEffect(() => {
    if (inView && section.status === 'ready' && section.experiences.length > 0 && impressedList.current !== tracking.listId) {
      impressedList.current = tracking.listId
      trackInteraction('impression')
    }
  }, [inView, section.status, section.experiences.length, tracking.listId, trackInteraction])

  const handleViewAllClick = useCallback(() => trackInteraction('view_all_clicked'), [trackInteraction])
  const handleArrowClick = useCallback((_: MouseEvent<HTMLButtonElement>, direction: ArrowDirection) => {
    trackInteraction(direction === 'right' ? 'carousel_next_clicked' : 'carousel_prev_clicked')
  }, [trackInteraction])
  const handleListEvent = useCallback<OfferListEventHandler>((event) => {
    if (section.status === 'ready' && event.type === OfferListEvents.productClick && !event.isRightClick && Number.isInteger(event.position) && event.position >= 0 && event.position < section.experiences.length) {
      trackInteraction('tile_clicked', event.position + 1)
    }
  }, [section.status, section.experiences.length, trackInteraction])

  return <Section ref={trackingRef} data-testid={`experience-recommendations-${section.id}`}>
    <Group horizontalAlign="space-between" verticalAlign="center" direction="horizontal">
      <Heading as="h4" variant="heading3">{intl.formatMessage(titles[section.id])}</Heading>
      <TextButton kind="tertiary" horizontalOutdent="end" to={searchUrl} onClick={handleViewAllClick}>
        {intl.formatMessage({
          id: '5Eysvi8V-dGKyoKYbpKw',
          defaultMessage: 'View all',
          description: 'Link to view all similar experience offers on PDP on the offer detail page',
        })}
      </TextButton>
    </Group>
    <OfferListEventsProvider tracking={tracking} onListEvent={handleListEvent}>
      <ExperienceListTileCarousel
        experiences={section.experiences}
        listFetching={section.status === 'loading'}
        listKey={section.listKey}
        showCancellation={false}
        showGifting={false}
        onArrowClick={handleArrowClick}
      />
    </OfferListEventsProvider>
  </Section>
}

interface Props {
  experience: App.ExperienceOffer
}

function ExperienceOfferPageRecommendations({ experience }: Props) {
  const { place, sections } = useExperienceOfferPageRecommendations(experience)
  const visibleSections = sections.filter(section => section.status !== 'hidden')
  if (!place || visibleSections.length === 0) return null

  return <RecommendationsContainer>
    {visibleSections.map(section => <RecommendationCarousel
      key={section.id}
      section={section}
      place={place}
      experienceId={experience.id}
    />)}
  </RecommendationsContainer>
}

export default ExperienceOfferPageRecommendations
